import React from "react";
import { Typography, CircularProgress, Snackbar } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { Modal } from "@shared/components";
import { useAgreementTokenModalController } from "./controller";
import { AgreementTokenModalProps } from "./types";
import {
  ErrorContainer,
  LoadingContainer,
  StyledAlert,
  TokenBox,
  TokenContainer,
  TokenText
} from "./styles";

export const AgreementTokenModal = React.memo(({
  modalRef,
  token,
  isLoading,
  error,
  onClose
}: AgreementTokenModalProps) => {
  const {
    openSnackbar,
    translate,
    handleCopy,
    handleCloseSnackbar,
  } = useAgreementTokenModalController({ token });

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <CircularProgress
            size={48}
            thickness={4}
            sx={{ color: 'primary.main' }}
          />
          <Typography variant="body1" color="text.secondary">
            {translate("v1/agreement_admin.messages.token.generating")}
          </Typography>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <Typography variant="h6" color="error" gutterBottom>
            {translate("v1/agreement_admin.messages.token.error")}
          </Typography>
          <Typography color="text.secondary">
            {error}
          </Typography>
        </ErrorContainer>
      );
    }

    if (token) {
      return (
        <TokenBox onClick={handleCopy}>
          <TokenText>{token}</TokenText>
        </TokenBox>
      );
    }

    return null;
  };

  return (
    <>
      <Modal
        ref={modalRef}
        title={translate("v1/agreement_admin.messages.token.title")}
        cancelLabel={translate("buttons.close")}
        hideConfirmButton
      >
        <TokenContainer>
          {renderContent()}
        </TokenContainer>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <StyledAlert
          onClose={handleCloseSnackbar}
          severity="success"
          icon={<ContentCopyIcon fontSize="inherit" />}
        >
          {translate("v1/agreement_admin.messages.token.copied")}
        </StyledAlert>
      </Snackbar>
    </>
  );
});
